import React, { Fragment } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { sizes } from '../constants/companySize';

export const isCustomer = (customerType) =>
  ['TRIAL', 'PAYMENTTYPE1', 'CUSTOMER'].includes(customerType);

export const isCustomerOrPost = (customerType) =>
  ['TRIAL', 'PAYMENTTYPE1', 'CUSTOMER', 'POST_TRIAL', 'POST_CUSTOMER'].includes(
    customerType
  );

export const isCustomerOrAffiliate = (customerType) =>
  ['TRIAL', 'PAYMENTTYPE1', 'CUSTOMER', 'AFFILIATE'].includes(customerType);

export const mailProviders = [
  'gmail.com',
  'gmail.se',
  'outlook.com',
  'yahoo.com',
  'yahoo.con',
  'yahoo.dk',
  'yahoo.no',
  'yahoo.se',
  'yahoo.gr',
  'hotmail.com',
  'hotmail.se',
  'test.com',
  'test.se',
  'live.com',
  'live.se',
  'live.no',
  'live.dk',
  'live.fi',
  'mail.com',
  'bredband.net',
  'tempinbox.org',
  'telia.se',
  'telia.com',
  'icloud.com',
  'me.com',
  'online.no',
  'online.com',
  'proton.me',
  'proxiedmail.com',
  'provar.se',
  'test.com',
  'test.se',
  'test.no',
  'test.dk',
  'test.nu',
  'foretag.se',
  'foretag.com',
  'foretaget.se',
  'foretag.nu',
  'foretagsam.se',
  'företag.se',
  'företag.no',
  'føretag.no',
  'foretag.no',
  'företag.com',
];

export const getCategoriesMap = (allCategoriesArr) =>
  allCategoriesArr.reduce((acc, curr) => {
    acc[curr.categoryDbName] = curr;
    return acc;
  }, {});

export const getStructuredDataRating = (
  ratingBW,
  ratingReviews,
  customerType
) => {
  const isPaidCustomer = isCustomer(customerType);
  const floatRatingBW = parseFloat(ratingBW || 0, 10);
  const floatRatingReviews = parseFloat(ratingReviews || 0, 10);

  // Vendor is not paid customer. Return use review rating
  if (!isPaidCustomer) {
    return floatRatingReviews;
  }

  // If vendor is paid customer, return highest of these two
  const highestRating = Math.max(floatRatingBW, floatRatingReviews);

  return highestRating > 5 ? 5 : highestRating;
};

export function getSizeRange(sizeRanges) {
  if (sizeRanges.length === 1 && sizeRanges[0] === 'LARGE') {
    return '500+';
  }

  let smallest = Infinity;
  let largest = -Infinity;

  for (const sizeRange of sizeRanges) {
    if (sizes[sizeRange]) {
      const rangeValue = sizes[sizeRange];
      if (rangeValue === '500+') {
        smallest = Math.min(smallest, 500);
        largest = Infinity;
      } else {
        const [min, max] = rangeValue.split('-');
        smallest = Math.min(smallest, parseInt(min));
        largest = Math.max(largest, parseInt(max));
      }
    }
  }

  if (largest === Infinity) {
    return `${smallest}-500+`;
  }
  return `${smallest}-${largest}`;
}

export const removeHTMLAndLineBreak = (textWithHTML) => {
  if (!textWithHTML) return '';

  const noHTML = textWithHTML.replace(/<[^>]*>/g, '');
  const noLineBreaks = noHTML.replace(/\n/g, ' ');
  return noLineBreaks;
};

export const createUrlPath = (pathArr) => {
  const pathArray = [...pathArr];

  pathArray.splice(0, 0, '/');
  pathArray.push('/');

  const pathString = pathArray.join('/');

  return pathString.replace(/\/+/g, '/');
};

export const getArrayItemsToStringNice = (categoriesNamesArr, t) => {
  if (categoriesNamesArr.length === 1) {
    return categoriesNamesArr[0];
  }

  const lastCategory = categoriesNamesArr.pop();

  const mergedNiceNames = categoriesNamesArr.join(', ');

  return `${mergedNiceNames} ${t('formCommon.and')} ${lastCategory}`;
};

export function cnMerge(...inputs) {
  return twMerge(clsx(inputs));
}

export const getDataByType = (sections, type) => {
  const section = sections.find(
    (section) => section.component && section.component.type === type
  );
  return section ? section.component : null;
};

export const getComponentsByType = (sections, type) => {
  const section = sections.find((section) => section.type === type);
  return section ? section.components : null;
};

export function splitStringEqually(text) {
  const words = text.split(' ');
  const middleIndex = Math.floor(words.length / 2);
  const firstPart = words.slice(0, middleIndex).join(' ');
  const secondPart = words.slice(middleIndex).join(' ');
  return [firstPart, secondPart];
}

export const addLineBreak = (str, breakCount = 1) =>
  str.split('\n').map((subStr, lineIndex) => {
    const elements = [];
    elements.push(<Fragment key={`line_${lineIndex}`}>{subStr}</Fragment>);

    for (let i = 0; i < breakCount; i++) {
      elements.push(<br key={`br_${lineIndex}_${i}`} />);
    }

    return elements;
  });

export const groupByProperty = (array, propertyName) => {
  const grouped = {};
  array.forEach((item) => {
    const groupKey = item[propertyName];
    if (!grouped[groupKey]) {
      grouped[groupKey] = [item];
    } else {
      grouped[groupKey].push(item);
    }
  });
  return Object.values(grouped);
};

export const formatNumberWithSpaces = (number) => {
  const formattedNumber = number.toLocaleString('en-US', {
    minimumIntegerDigits: 1,
    useGrouping: true,
  });
  const spacedNumber = formattedNumber.replace(/,/g, ' ');
  return spacedNumber;
};

export const getCategoryName = (dbName, data) => {
  if (data && dbName) {
    for (const subCategories of Object.values(data)) {
      for (const categoryObj of subCategories) {
        if (categoryObj.dbName === dbName) {
          return categoryObj.name;
        }
      }
    }
  }

  return null;
};

export const convertMarkdownToHtml = (text) =>
  text ? text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') : '';

export const getColorClass = () => {
  const color = 'bg-white';
  return color;

  // switch (i) {
  //   case 0:
  //     return 'bg-white';
  //   case 1:
  //     return 'bg-primary-100';
  //   case 2:
  //     return 'bg-[#DFEEF9]';
  //   default:
  //     return 'bg-white';
  // }
};

const textToReadingMinutes = (text, nearest = 0) => {
  const numberOfWords = text.split(' ').length;

  let numberOfMinutes = numberOfWords / 238;

  if (nearest > 1 && numberOfMinutes > nearest) {
    numberOfMinutes = Math.round(numberOfMinutes / nearest) * nearest;
  } else if (numberOfMinutes <= nearest) {
    numberOfMinutes = Math.ceil(numberOfMinutes);
  }

  return numberOfMinutes;
};

export const textToReadingMinutesFormattedText = ({ text, nearest = 0, t }) => {
  const numberOfMinutes = textToReadingMinutes(text, nearest);

  if (numberOfMinutes >= 60) {
    const hours = Math.floor(numberOfMinutes / 60);
    const minutes = Math.round(numberOfMinutes % 60);

    return t('blog.readingTimeAboveHour', { hours, minutes });
  }
  const minutes = numberOfMinutes;
  return t('blog.readingTimeMinutes', { minutes });
};

/**
 * Checks for banner default option
 * @param headerSection
 * @returns {*|boolean|boolean}
 */
export const isBannerConfigImageBackground = (
  isBannerAllowed,
  headerSection
) => {
  if (isBannerAllowed) {
    return headerSection.componentData?.bannerConfig?.imageBackground !==
      undefined
      ? headerSection.componentData?.bannerConfig?.imageBackground
      : true;
  }
  return false;
};

/**
 * Banner configuration for background image effect
 * @param headerSection
 * @returns {{backgroundColor: (*|string)}|{position: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string}}
 */
export const getSystemBannerBackgroundConfig = (
  isBannerAllowed,
  headerSection
) => {
  if (isBannerAllowed) {
    const isImageBackground = isBannerConfigImageBackground(
      isBannerAllowed,
      headerSection
    );

    if (isImageBackground) {
      return {
        position: 'relative',
        backgroundImage: `url("${headerSection.componentData.bannerUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    }
  }
  return {
    backgroundColor:
      isBannerAllowed && headerSection.componentData?.bannerConfig?.color
        ? headerSection.componentData?.bannerConfig?.color
        : '#ffffff',
  };
};
